import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { useSupabase } from './SupabaseContext'
import { UserProfile, MembershipType } from '../types'
import { sendWelcomeEmail } from '../lib/email'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  profileLoading: boolean
  userProfile: UserProfile | null
  membershipType: MembershipType
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const supabase = useSupabase()
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(true)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)

  const fetchUserProfile = async (userId: string) => {
    setProfileLoading(true)
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) {
        console.error('Error fetching profile:', error)
        setUserProfile(null)
      } else if (data) {
        setUserProfile(data as UserProfile)
      } else {
        // If no profile exists yet, create one with default free membership
        const { data: createdProfile, error: insertError } = await supabase
          .from('user_profiles')
          .insert({ id: userId })
          .select()
          .maybeSingle()

        if (insertError) {
          console.error('Error creating profile:', insertError)
          setUserProfile(null)
        } else {
          setUserProfile(createdProfile as UserProfile)
        }
      }
    } catch (err) {
      console.error('Unexpected error fetching profile:', err)
      setUserProfile(null)
    } finally {
      setProfileLoading(false)
    }
  }

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error('Error getting session:', error)
        }
        setSession(session)
        setUser(session?.user ?? null)
        if (session?.user) {
          fetchUserProfile(session.user.id)
        } else {
          setUserProfile(null)
          setProfileLoading(false)
        }
        setLoading(false)
      })
      .catch((error) => {
        console.error('Failed to get session:', error)
        setLoading(false)
      })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Skip redundant updates when tab regains focus - Supabase fires SIGNED_IN/TOKEN_REFRESHED
      // even when nothing changed, causing unnecessary dashboard reloads
      setSession((prev) => {
        if (prev?.access_token === newSession?.access_token && prev?.user?.id === newSession?.user?.id) {
          return prev
        }
        return newSession
      })
      setUser((prev) => {
        const newUser = newSession?.user ?? null
        if (prev?.id === newUser?.id && prev != null) return prev
        return newUser
      })
      if (newSession?.user) {
        setUserProfile((prev) => {
          if (prev?.id === newSession.user.id) return prev
          fetchUserProfile(newSession.user.id)
          return prev
        })
      } else {
        setUserProfile(null)
        setProfileLoading(false)
      }
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [supabase])

  const signUp = async (email: string, password: string) => {
    // Get the current origin for email redirect
    const redirectTo = typeof window !== 'undefined' 
      ? `${window.location.origin}/auth/callback`
      : undefined

    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo,
      },
    })

    // Send welcome email if signup was successful
    if (!error && data.user) {
      try {
        await sendWelcomeEmail(email, data.user.email)
      } catch (emailError) {
        // Log but don't fail the signup if email fails
        console.error('Failed to send welcome email:', emailError)
      }
    }

    return { error }
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setUserProfile(null)
  }

  const refreshProfile = async () => {
    if (user?.id) {
      await fetchUserProfile(user.id)
    }
  }

  // Calculate membership type, checking if pro membership has expired
  const membershipType: MembershipType = (() => {
    if (!userProfile) return 'free'
    
    // Handle publisher membership type
    if (userProfile.membership_type === 'publisher') {
      return 'publisher'
    }
    
    if (userProfile.membership_type === 'pro') {
      // Check if pro_expiry exists and has not passed
      if (userProfile.pro_expiry) {
        const expiryDate = new Date(userProfile.pro_expiry)
        const now = new Date()
        if (expiryDate > now) {
          return 'pro'
        }
      } else {
        // If no expiry date, assume it's a permanent pro membership (e.g., from Stripe subscription)
        return 'pro'
      }
    }
    return 'free'
  })()

  const value = {
    user,
    session,
    loading,
    profileLoading,
    userProfile,
    membershipType,
    signUp,
    signIn,
    signOut,
    refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
